import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  useWindowDimensions,
  Platform,
  Alert,
  ScrollView,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useFocusEffect } from "@react-navigation/native";
import { AuthContext } from "../Context/AuthContext";
import DownloadReport from "../components/PersonalReport";
import GlobalStyles from "../components/StylenColor";
import { collection, getDocs, addDoc } from "firebase/firestore";
import { db } from "../firebase"; // Adjust path if needed
import { Ionicons, Feather } from "@expo/vector-icons";
import { useDesktopDetect, EmployeeLayout, DesktopTheme, getScoreColor } from "../components/web";

const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1;
  if (month <= 3) return "Q1";
  if (month <= 6) return "Q2";
  if (month <= 9) return "Q3";
  return "Q4";
};

// Desktop Topic Card Component
const DesktopTopicCard = ({ topic, isFilled, topicScore, onPress, onDownload, selectedQuarter }) => {
  const [isHovered, setIsHovered] = useState(false);
  const scorePercent = topicScore?.maxScore
    ? Math.round((topicScore.totalScore / topicScore.maxScore) * 100)
    : null;
  const scoreColor = scorePercent !== null ? getScoreColor(scorePercent) : DesktopTheme.colors.textMuted;

  return (
    <TouchableOpacity
      style={[
        desktopStyles.topicCard,
        isHovered && desktopStyles.topicCardHover,
      ]}
      onPress={onPress}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <View style={desktopStyles.topicCardContent}>
        <View style={desktopStyles.topicIconContainer}>
          {isFilled ? (
            <View style={[desktopStyles.statusIcon, desktopStyles.statusFilled]}>
              <Ionicons name="checkmark" size={20} color="#ffffff" />
            </View>
          ) : (
            <View style={[desktopStyles.statusIcon, desktopStyles.statusPending]}>
              <Ionicons name="time-outline" size={20} color="#ffffff" />
            </View>
          )}
        </View>
        <View style={desktopStyles.topicInfo}>
          <Text style={desktopStyles.topicTitle}>{topic}</Text>
          <View style={desktopStyles.topicMetaRow}>
            <Text style={[
              desktopStyles.topicStatus,
              { color: isFilled ? DesktopTheme.colors.success : DesktopTheme.colors.warning }
            ]}>
              {isFilled ? 'Completed' : 'Pending'}
            </Text>
            {isFilled && topicScore?.maxScore ? (
              <View
                style={[
                  desktopStyles.scoreBadge,
                  { borderColor: scoreColor, backgroundColor: `${scoreColor}15` },
                ]}
              >
                <Ionicons
                  name="stats-chart-outline"
                  size={12}
                  color={scoreColor}
                  style={desktopStyles.scoreBadgeIcon}
                />
                <Text style={[desktopStyles.scoreBadgeText, { color: scoreColor }]}>
                  {topicScore.totalScore} / {topicScore.maxScore}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
      <View style={desktopStyles.topicActions}>
        <DownloadReport
          iconSize={24}
          iconColor={DesktopTheme.colors.primary}
          topic={topic}
          selectedQuarter={selectedQuarter}
        />
        <Ionicons
          name="chevron-forward"
          size={20}
          color={DesktopTheme.colors.textMuted}
          style={{ marginLeft: 8 }}
        />
      </View>
    </TouchableOpacity>
  );
};

// Desktop View Component
const DesktopTopicsView = ({
  user,
  topics,
  filledTopics,
  topicScores,
  error,
  isLoading,
  handleTopicPress,
  emailSupport,
  callSupport,
  onLogout,
}) => {
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const currentQuarter = getCurrentQuarter();

  const filledCount = filledTopics.length;
  const totalCount = topics.length;
  const pendingCount = totalCount - filledCount;
  const progressPercent = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;

  return (
    <EmployeeLayout
      title="My Topics"
      subtitle={`${user?.department || 'Your Department'} - ${currentQuarter}`}
      userName={user?.name || user?.email}
      companyName={user?.companyName}
      onLogout={onLogout}
    >
      <ScrollView style={desktopStyles.container} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={desktopStyles.header}>
          <View style={desktopStyles.headerLeft}>
            <Text style={desktopStyles.pageTitle}>My Topics</Text>
            <Text style={desktopStyles.pageSubtitle}>
              {user?.department || 'Your Department'} - {currentQuarter}
            </Text>
          </View>
        </View>

        <View style={desktopStyles.contentGrid}>
          <View style={desktopStyles.leftColumn}>
            {/* Stats */}
            <View style={desktopStyles.statsGrid}>
              <View style={desktopStyles.statCard}>
                <Ionicons name="documents-outline" size={24} color={DesktopTheme.colors.primary} />
                <Text style={desktopStyles.statValue}>{totalCount}</Text>
                <Text style={desktopStyles.statLabel}>Total Topics</Text>
              </View>
              <View style={desktopStyles.statCard}>
                <Ionicons name="checkmark-done-outline" size={24} color={DesktopTheme.colors.success} />
                <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.success }]}>{filledCount}</Text>
                <Text style={desktopStyles.statLabel}>Completed</Text>
              </View>
              <View style={desktopStyles.statCard}>
                <Ionicons name="time-outline" size={24} color={DesktopTheme.colors.warning} />
                <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.warning }]}>{pendingCount}</Text>
                <Text style={desktopStyles.statLabel}>Pending</Text>
              </View>
              <View style={desktopStyles.statCard}>
                <Ionicons name="analytics-outline" size={24} color={DesktopTheme.colors.info} />
                <Text style={[desktopStyles.statValue, { color: DesktopTheme.colors.info }]}>{progressPercent}%</Text>
                <Text style={desktopStyles.statLabel}>Progress</Text>
              </View>
            </View>

            {/* Progress Bar */}
            <View style={desktopStyles.progressCard}>
              <View style={desktopStyles.progressHeader}>
                <Text style={desktopStyles.progressTitle}>Completion Progress</Text>
                <Text style={desktopStyles.progressPercent}>{progressPercent}%</Text>
              </View>
              <View style={desktopStyles.progressBarBg}>
                <View style={[desktopStyles.progressBarFill, { width: `${progressPercent}%` }]} />
              </View>
            </View>
          </View>

          <View style={desktopStyles.rightColumn}>
            {/* Content */}
            {isLoading ? (
              <View style={desktopStyles.loadingContainer}>
                <ActivityIndicator size="large" color={DesktopTheme.colors.primary} />
                <Text style={desktopStyles.loadingText}>Loading topics...</Text>
              </View>
            ) : error ? (
              <View style={desktopStyles.errorContainer}>
                <Ionicons name="alert-circle-outline" size={48} color={DesktopTheme.colors.error} />
                <Text style={desktopStyles.errorTitle}>No Topics Assigned</Text>
                <Text style={desktopStyles.errorText}>{error}</Text>
              </View>
            ) : (
              <View style={desktopStyles.topicsSection}>
                <Text style={desktopStyles.sectionTitle}>Your Assigned Topics</Text>
                <View style={desktopStyles.topicsGrid}>
                  {topics.map((topic) => (
                    <DesktopTopicCard
                      key={topic}
                      topic={topic}
                      isFilled={filledTopics.includes(topic)}
                      topicScore={topicScores[topic]}
                      onPress={() => handleTopicPress(topic)}
                      selectedQuarter={currentQuarter}
                    />
                  ))}
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Support Section */}
        <View style={desktopStyles.supportSection}>
          <Text style={desktopStyles.supportTitle}>Need Help?</Text>
          <Text style={desktopStyles.supportText}>
            Contact our support team if you have any questions about your assigned topics.
          </Text>
          <View style={desktopStyles.supportButtons}>
            <TouchableOpacity
              style={[
                desktopStyles.supportBtn,
                hoveredBtn === 'email' && desktopStyles.supportBtnHover,
              ]}
              onPress={emailSupport}
              onMouseEnter={() => setHoveredBtn('email')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <Ionicons name="mail-outline" size={20} color="#ffffff" />
              <Text style={desktopStyles.supportBtnText}>Email Support</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                desktopStyles.supportBtnSecondary,
                hoveredBtn === 'call' && desktopStyles.supportBtnSecondaryHover,
              ]}
              onPress={callSupport}
              onMouseEnter={() => setHoveredBtn('call')}
              onMouseLeave={() => setHoveredBtn(null)}
            >
              <Ionicons name="call-outline" size={20} color={DesktopTheme.colors.primary} />
              <Text style={desktopStyles.supportBtnSecondaryText}>Call Support</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </EmployeeLayout>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: DesktopTheme.spacing.lg,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  contentGrid: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: DesktopTheme.spacing.lg,
    marginBottom: DesktopTheme.spacing.xl,
  },
  leftColumn: {
    width: '30%',
    minWidth: 260,
    gap: DesktopTheme.spacing.lg,
  },
  rightColumn: {
    flex: 1,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.md,
  },
  statCard: {
    width: '48%',
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.md,
    padding: DesktopTheme.spacing.md,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  statValue: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginTop: DesktopTheme.spacing.xs,
  },
  statLabel: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
    marginTop: 2,
  },
  progressCard: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.md,
    padding: DesktopTheme.spacing.md,
    ...DesktopTheme.shadows.sm,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.sm,
  },
  progressTitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '600',
  },
  progressPercent: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.primary,
    fontWeight: '700',
  },
  progressBarBg: {
    height: 10,
    backgroundColor: DesktopTheme.colors.border,
    borderRadius: 5,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: DesktopTheme.colors.primary,
    borderRadius: 5,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    marginTop: DesktopTheme.spacing.md,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textSecondary,
  },
  errorContainer: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.xl,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  errorTitle: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginTop: DesktopTheme.spacing.md,
    marginBottom: DesktopTheme.spacing.sm,
  },
  errorText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    textAlign: 'center',
  },
  topicsSection: {
    marginBottom: DesktopTheme.spacing.xl,
  },
  sectionTitle: {
    fontSize: DesktopTheme.typography.fontSize.h4,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: DesktopTheme.spacing.md,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.md,
  },
  topicCard: {
    flexBasis: '48%',
    minWidth: 240,
    minHeight: 130,
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.md,
    borderWidth: 1,
    borderColor: DesktopTheme.colors.border,
    padding: DesktopTheme.spacing.md,
    ...DesktopTheme.shadows.sm,
  },
  topicCardHover: {
    ...DesktopTheme.shadows.md,
    borderColor: DesktopTheme.colors.primary,
    transform: [{ translateY: -2 }],
  },
  topicCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  topicIconContainer: {
    marginRight: DesktopTheme.spacing.md,
  },
  statusIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statusFilled: {
    backgroundColor: DesktopTheme.colors.success,
  },
  statusPending: {
    backgroundColor: DesktopTheme.colors.warning,
  },
  topicInfo: {
    flex: 1,
  },
  topicTitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '600',
    marginBottom: 4,
  },
  topicStatus: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
  },
  topicMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    flexWrap: 'wrap',
  },
  scoreBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: DesktopTheme.borderRadius.full,
    borderWidth: 1,
  },
  scoreBadgeIcon: {
    marginRight: 4,
  },
  scoreBadgeText: {
    fontSize: DesktopTheme.typography.fontSize.tiny,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontWeight: '600',
  },
  topicActions: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginTop: DesktopTheme.spacing.sm,
  },
  supportSection: {
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.lg,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  supportTitle: {
    fontSize: DesktopTheme.typography.fontSize.h4,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: DesktopTheme.spacing.sm,
  },
  supportText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: DesktopTheme.spacing.lg,
    maxWidth: 400,
  },
  supportButtons: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.md,
  },
  supportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.primary,
    paddingVertical: DesktopTheme.spacing.md,
    paddingHorizontal: DesktopTheme.spacing.lg,
    borderRadius: DesktopTheme.borderRadius.md,
  },
  supportBtnHover: {
    backgroundColor: DesktopTheme.colors.primaryHover,
  },
  supportBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: '#ffffff',
    fontWeight: '600',
  },
  supportBtnSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.primaryLight,
    paddingVertical: DesktopTheme.spacing.md,
    paddingHorizontal: DesktopTheme.spacing.lg,
    borderRadius: DesktopTheme.borderRadius.md,
    borderWidth: 1,
    borderColor: DesktopTheme.colors.primary,
  },
  supportBtnSecondaryHover: {
    backgroundColor: DesktopTheme.colors.border,
  },
  supportBtnSecondaryText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.primary,
    fontWeight: '600',
  },
});

const TopicsScreen = ({ navigation }) => {
  const { user, companyId, logoutCompany, setUser, setCompanyId, setCompanyName } = useContext(AuthContext);
  const [topics, setTopics] = useState([]);
  const [filledTopics, setFilledTopics] = useState([]);
  const [topicScores, setTopicScores] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const isDesktopWeb = useDesktopDetect();

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };

  const fetchTopics = async () => {
    setIsLoading(true);
    try {
      if (user?.topic && Array.isArray(user.topic)) {
        setTopics(user.topic);
        await checkFilledTopics(user.topic);
        setError(null);
      } else {
        setError("No topic assigned to your profile.");
        setTopics([]);
      }
    } catch (err) {
      console.error("Error fetching topic data:", err);
      setError("Failed to load topics.");
    }
    setIsLoading(false);
  };

  const checkFilledTopics = async (topicList) => {
    const currentQuarter = getCurrentQuarter();
    if (!companyId || !user?.employeeId) return;

    const responsesRef = collection(
      db,
      `companies/${companyId}/questionnaireResponses`
    );
    const snapshot = await getDocs(responsesRef);

    const filledSet = new Set();
    const scoreMap = {};

    snapshot.forEach((doc) => {
      const data = doc.data();
      const isForQuarter = data?.quarter === currentQuarter;
      const isForTopic = topicList.includes(data.topic);
      const isForEmployee =
        data?.userId === user.employeeId ||
        data?.employeeId === user.employeeId ||
        data?.employeeIdNum === user.employeeIdNum;
      if (!isForQuarter || !isForTopic || !isForEmployee) return;

      filledSet.add(data.topic);

      const totalScoreValue = Number(data.totalScore);
      const questionCount = data.NumberOfQuestions || (Array.isArray(data.responses) ? data.responses.length : null);
      const maxScore = questionCount ? questionCount * 5 : null;
      const timestamp = data.timestamp?.toDate ? data.timestamp.toDate() : data.timestamp;
      const timestampMs = timestamp ? new Date(timestamp).getTime() : 0;
      const existing = scoreMap[data.topic];

      if (!existing || timestampMs > existing.timestampMs) {
        const safeTotalScore = Number.isFinite(totalScoreValue) ? totalScoreValue : 0;
        scoreMap[data.topic] = {
          totalScore: safeTotalScore,
          maxScore,
          timestampMs,
        };
      }
    });

    setFilledTopics(Array.from(filledSet));
    setTopicScores(scoreMap);
  };

  useFocusEffect(
    useCallback(() => {
      fetchTopics();
    }, [user, companyId])
  );

  const handleTopicPress = (topic) => {
    navigation.navigate("QuestionsScreen", { topic });
  };

  const emailSupport = async () => {
    const emailTo = "sowingroups@gmail.com";
    const subject = "Help Needed with Topics - Employee Support";
    const body = `Dear Support Team,\n\nI need assistance with the Topics screen.\n\nMy Employee ID: ${
      user?.employeeId || "N/A"
    }\nMy Department: ${
      user?.department || "N/A"
    }\n\n[Please describe your issue here]\n\nThank you.`;

    // Construct the Gmail web URL directly
    // fs=1 forces the "full screen" composer in new window/tab
    const gmailWebUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
      emailTo
    )}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    try {
      const supported = await Linking.canOpenURL(gmailWebUrl);
      if (supported) {
        await Linking.openURL(gmailWebUrl);
      } else {
        // Fallback for extremely rare cases where browser cannot be opened
        customAlert(
          "Cannot Open Gmail",
          "Please open your browser and go to mail.google.com to send an email to tanmaygharat957@gmail.com."
        );
      }
    } catch (err) {
      console.error("Error opening Gmail:", err);
      customAlert(
        "Error",
        "Could not open Gmail. Please try again or contact support directly."
      );
    }
  };

  const callSupport = () => {
    Linking.openURL("tel:+919223043047"); // Replace with your support number
  };

  const handleEmployeeLogout = async () => {
    try {
      setUser(null);
      setCompanyId(null);
      setCompanyName(null);
      await logoutCompany();
    } catch (error) {
      console.error("Error logging out employee:", error);
    } finally {
      navigation.reset({
        index: 0,
        routes: [{ name: "EmployeeLoginScreen" }],
      });
    }
  };

  // Desktop Web View
  if (isDesktopWeb) {
    return (
      <DesktopTopicsView
        user={user}
        topics={topics}
        filledTopics={filledTopics}
        topicScores={topicScores}
        error={error}
        isLoading={isLoading}
        handleTopicPress={handleTopicPress}
        emailSupport={emailSupport}
        callSupport={callSupport}
        onLogout={handleEmployeeLogout}
      />
    );
  }

  if (isLoading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  // Mobile View
  return (
    <LinearGradient // Use LinearGradient for the background
      colors={GlobalStyles.GlobalBg.gradientColors}
      style={{ flex: 1 }}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <View
        style={[styles.container, isDesktop && styles.containerDesktopPadding]}
      >
        <Text style={[styles.title, GlobalStyles.titleTxtOffset]}>
          Topics for {user?.department || "your department"}
        </Text>

        {error ? (
          <Text style={styles.errorText}>{error}</Text>
        ) : (
          <FlatList
            data={topics}
            keyExtractor={(item) => item} // Using item as key assuming topics are unique
            key={isDesktop ? "h" : "v"} // Change key to force re-render on orientation change
            numColumns={isDesktop ? 2 : 1}
            columnWrapperStyle={
              isDesktop ? styles.columnWrapper : null // Apply styles.columnWrapper
            }
            contentContainerStyle={styles.flatListContent} // Add contentContainerStyle
            renderItem={({ item }) => {
              const isFilled = filledTopics.includes(item);
              const scoreData = topicScores[item];
              const scorePercent = scoreData?.maxScore
                ? Math.round((scoreData.totalScore / scoreData.maxScore) * 100)
                : null;
              const scoreColor = scorePercent !== null ? getScoreColor(scorePercent) : GlobalStyles.btnColor.backgroundColor;
              return (
                <TouchableOpacity
                  onPress={() => handleTopicPress(item)}
                  style={[
                    styles.topicCard,
                    isDesktop && styles.desktopCard,
                    GlobalStyles.btnBgShadow, // Apply subtle shadow to cards
                  ]}
                >
                  <View style={styles.topicTextContainer}>
                    <Text style={styles.topicTitle}>{item}</Text>
                    {isFilled && scoreData?.maxScore ? (
                      <View
                        style={[
                          styles.scoreBadge,
                          { borderColor: scoreColor, backgroundColor: `${scoreColor}18` },
                        ]}
                      >
                        <Ionicons
                          name="stats-chart-outline"
                          size={12}
                          color={scoreColor}
                          style={styles.scoreBadgeIcon}
                        />
                        <Text style={[styles.scoreBadgeText, { color: scoreColor }]}>
                          {scoreData.totalScore} / {scoreData.maxScore}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                  <View style={styles.topicStatusContainer}>
                    <Feather
                      name={isFilled ? "check-circle" : "x-octagon"} // Changed icons
                      size={25}
                      color={isFilled ? "#28a745" : "#dc3545"}
                      style={styles.statusIndicator}
                    />
                    <DownloadReport
                      iconSize={30} // Slightly smaller icon for better integration
                      iconColor={GlobalStyles.btnColor.backgroundColor} // Use consistent color
                      topic={item}
                      selectedQuarter={getCurrentQuarter()}
                    />
                  </View>
                </TouchableOpacity>
              );
            }}
            ListFooterComponent={
              <Text style={styles.summaryText}>
                {filledTopics.length} filled / {topics.length} total
              </Text>
            }
          />
        )}
        <View style={{ flexDirection: "row", justifyContent: "space-evenly", width: "100%" }}>
          <TouchableOpacity
            style={[styles.supportButton, GlobalStyles.btnBgShadow]}
            onPress={emailSupport}
          >
            <Ionicons
              name="mail"
              size={20}
              color="#fff"
              style={[GlobalStyles.btntxtoffset, { marginRight: 8 }]}
            />
            <Text style={[styles.supportButtonText, GlobalStyles.btntxtoffset]}>
              Email Support
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.supportButton, GlobalStyles.btnBgShadow]}
            onPress={callSupport}
          >
            <Ionicons
              name="call-outline"
              size={20}
              color="#fff"
              style={[GlobalStyles.btntxtoffset, { marginRight: 8 }]}
            />
            <Text style={[styles.supportButtonText, GlobalStyles.btntxtoffset]}>
              Call Support
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  title: {
    fontSize: GlobalStyles.fontSize.xl,
    color: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 1,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 16,
  },
  topicCard: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 18, // Increased padding
    marginBottom: 15, // More space between cards
    backgroundColor: GlobalStyles.inpColor.colors.background, // Use a lighter background from GlobalStyles
    borderRadius: 12, // More rounded corners
    // elevation and shadows handled by GlobalStyles.btnBgShadow
    marginHorizontal: Platform.OS === "web" ? 5 : 0, // Add horizontal margin for web to prevent cards from touching
  },
  desktopCard: {
    flexBasis: "48%", // Takes up one column
  },
  desktopCard: {
    marginRight: 10,
    minWidth: "48%",
  },
  topicTitle: {
    fontSize: GlobalStyles.fontSize.topTitle,
    fontWeight: "600",
    fontFamily: GlobalStyles.inpFont.fontFamily,
    paddingVertical: 1,
  },
  summaryText: {
    fontSize: GlobalStyles.fontSize.sm,
    textAlign: "center",
    marginTop: 10,
    color: "#555",
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  errorText: {
    color: "red",
    fontSize: GlobalStyles.fontSize.xss,
    textAlign: "center",
  },
  supportButton: {
    marginTop: 20,
    paddingHorizontal: 20,
    width: "48%",
    maxHeight: 60,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 15,
    borderRadius: 8,
    bottom: 15,
  },
  supportButtonText: {
    color: "#fff",
    paddingVertical: 2,
    fontSize: GlobalStyles.fontSize.sm,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  flatListContent: {
    paddingBottom: 20, // Add some padding at the bottom of the list
  },
  columnWrapper: {
    justifyContent: "space-between",
    marginBottom: 15, // Space between rows in desktop
  },
  topicStatusContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  statusIndicator: {
    marginRight: 8, // Space between check/cross and download icon
    // No fontWeight: 'bold' as per instruction
  },
  topicTextContainer: {
    flex: 1, // Allow text to take available space
    marginRight: 10, // Space between text and status/download
  },
  scoreBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    marginTop: 6,
    alignSelf: "flex-start",
  },
  scoreBadgeIcon: {
    marginRight: 4,
  },
  scoreBadgeText: {
    fontSize: GlobalStyles.fontSize.xss,
    fontFamily: GlobalStyles.inpFont.fontFamily,
    fontWeight: "600",
  },
});

export default TopicsScreen;
